import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { get } from 'lodash';
import { QuestionResponseDto, QuestionResponseTextDto } from 'models/dtoTypes';
import Box from '@mui/material/Box';
import SmeConfigContext from 'data/SmeConfigContext';
import SmeConfig from 'data/SmeConfig';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import LoadingButton from '@mui/lab/LoadingButton';
import { TextField, Button } from '@mui/material';
import { GetSection, GetUnsatisfiedSections, UpdateSmartGovernanceScore } from 'services/section';
import { date2str } from 'utils/date';
import axios from 'axios';
import SectionsValidationModal, {
  IUnsatisfiedSection,
} from 'kosmos/dialogs/SectionsValidationModal';
import { useTranslation } from 'react-i18next';
import AlertDialog from 'kosmos/dialogs/AlertDialog';
import React from 'react';

const PREFIX = 'ConfirmAndSubmitPage';

const classes = {
  content: `${PREFIX}-content`,
  boxContainer: `${PREFIX}-boxContainer`,
};

const StyledConfirmAndSubmitPage = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,

  [`& .${classes.content}`]: {
    display: 'flex',
    flexGrow: 1,
    backgroundColor: '#e7ebef',
  },

  [`& .${classes.boxContainer}`]: {
    flex: 1,
    borderRadius: 10,
    padding: 30,
    width: '100%',
    height: '100%',
    backgroundColor: 'white',
  },
}));

interface IProps {
  companyId: string;
  alias: string;
}

function ConfirmAndSubmitPage(props: IProps) {
  const alias = 'confirm-and-submit';
  const nameAlias = 'signed-by-name';
  const dateAlias = 'signed-at';
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [unsatisfiedSections, setUnsatisfiedSections] = useState<IUnsatisfiedSection[]>([]);
  const [openValidate, setOpenValidate] = useState(false);
  const [open, setOpen] = React.useState<boolean>(false);
  const [alert, setAlert] = React.useState<{
    header: string;
    message: string;
    button: string;
  }>({ header: '', message: '', button: '' });

  const { t } = useTranslation('default');

  // get the current section schema
  const fetchData = async () => {
    const response = await GetSection(alias, props.companyId);
    const data = response?.data;
    if (data) {
      const responses = data?.sectionResponse?.responses || [];
      const nameResponse = responses.find((r) => r.questionAlias === nameAlias);
      const dateResponse = responses.find((r) => r.questionAlias === dateAlias);
      if (nameResponse || dateResponse) {
        setName(String(get(nameResponse, 'value') || ''));
        setDate(String(get(dateResponse, 'value') || ''));
      } else {
        // no historical responses
        setIsEdit(true);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    void fetchData();
  }, []);

  const renderText = (para: string, index: number) => {
    return <p key={index}>{para}</p>;
  };

  const getUnsatisfiedSections = async (): Promise<IUnsatisfiedSection[]> => {
    const companyId = props.companyId;
    const unsatisfiedSections = await GetUnsatisfiedSections(companyId);
    const list: IUnsatisfiedSection[] = unsatisfiedSections.map((section) => {
      const { questions = [], alias = '', label = '' } = section;
      const questionAlias = section?.sectionResponse?.unsatisfiedQuestions || [];
      const fomrattedQuestions = questionAlias.map((questionAlias) => {
        const question = questions.find((q) => q.alias === questionAlias);
        return {
          questionAlias: question?.alias || '',
          questionLabel: question?.label || '',
        };
      });
      return {
        sectionAlias: alias || '',
        sectionLabel: label || '',
        questions: fomrattedQuestions,
      };
    });
    return list;
  };

  async function submitSmartGovernanceResponses() {
    const questionResponses: QuestionResponseTextDto[] = [
      {
        questionAlias: nameAlias,
        value: name,
        type: 'QuestionResponseTextDto',
      },
      {
        questionAlias: dateAlias,
        value: date2str(new Date()),
        type: 'QuestionResponseDateDto',
      },
    ];
    const data = await UpdateSmartGovernanceScore(alias, {
      sectionAlias: alias,
      companyId: props.companyId,
      questionResponses,
      subSectionResponses: [],
    });
    const responses = (data.responses || []) as QuestionResponseDto[];
    const nameResponse = responses.find((r) => r.questionAlias === nameAlias);
    const dateResponse = responses.find((r) => r.questionAlias === dateAlias);
    if (nameResponse || dateResponse) {
      setName(get(nameResponse, 'value') || '');
      setDate(get(dateResponse, 'value') || '');
    }
    setIsEdit(false);
    setAlert({
      header: 'Triggered',
      message: 'Smart Governance scoring is now in progress.',
      button: 'Ok',
    });
    setOpen(true);
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const unsatisfied = await getUnsatisfiedSections();
      if (unsatisfied?.length) {
        // some questions doesn't answered
        setUnsatisfiedSections(unsatisfied);
        setOpenValidate(true);
      } else {
        // validated and submit
        await submitSmartGovernanceResponses();
      }
    } catch (e) {
      console.error(e);
      let errorMsg = e instanceof Error ? e.message : 'Cannot trigger smart governance scoring.';
      if (axios.isAxiosError(e) && e.response) {
        console.log(e.response?.data);
        errorMsg = get(e.response?.data, 'message') || 'Cannot trigger smart governance scoring.';
      }

      setAlert({ header: 'Not Triggered', message: errorMsg, button: 'Ok' });
      setOpen(true);
    }

    setSubmitting(false);
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const renderButton = () => {
    if (isEdit) {
      return (
        <LoadingButton
          loading={submitting}
          disabled={!name}
          variant="contained"
          size="large"
          color="primary"
          onClick={handleSubmit}
        >
          {t('i-agree')}
        </LoadingButton>
      );
    } else {
      return (
        <Button
          size="large"
          type="submit"
          variant="contained"
          color="primary"
          aria-label="Signup"
          onClick={() => setIsEdit(true)}
        >
          Re-sign
        </Button>
      );
    }
  };

  const renderForm = (config: SmeConfig) => {
    console.log(config);
    const defaultDate = isEdit ? new Date() : new Date(date);
    return (
      <div>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextField
              sx={{ width: '100%' }}
              required
              id="name"
              label={t('name')}
              color="primary"
              value={name}
              disabled={!isEdit || submitting}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              sx={{ width: '100%' }}
              required
              id={t('date')}
              label={t('date')}
              color="primary"
              value={date2str(defaultDate)}
              disabled={true}
            />
          </Grid>
        </Grid>
        <Box sx={{ m: 3, textAlign: 'center' }}>{renderButton()}</Box>
      </div>
    );
  };

  const renderConfirmPage = (config: SmeConfig) => {
    const params = [
      t(
        'your-company-profile-is-determined-based-on-the-information-you-provide-under-the-following-terms',
      ),
      '',
      t(
        'you-are-required-to-exercise-due-diligence-in-ensuring-that-all-responses-are-complete-and-truthful-following-the-assignment-of-a-company-registration-profile-it-is-imperative-to-promptly-update-your-profile-should-any-of-the-provided-information-change-providing-outdated-or-inaccurate-information-may-lead-to-the-invalidation-of-registration-statuses-and-may-impact-compliance-reports',
      ),
      '',
      t('smart-governance-can-provide-further-insight-into-company-activity'),
      '',
      t(
        'every-statement-fact-and-document-submitted-through-the-registration-process-must-be-complete-truthful-and-accurate-if-the-forms-are-completed-by-someone-else-on-your-behalf-you-must-review-them-for-accuracy-and-completeness-prior-to-submission-it-is-essential-to-thoroughly-read-and-understand-the-declaration-before-agreeing-to-it',
      ),
      '',
      t(
        'i-hereby-declare-that-all-information-provided-is-accurate-and-truthful-to-the-best-of-my-knowledge-and-belief-and-i-commit-to-immediately-updating-the-company-profile-regarding-any-changes-therein',
      ),
    ];
    // TODO: should disable submit unless all other sectiosn are completed
    return (
      <StyledConfirmAndSubmitPage>
        <div className={classes.content}>
          <Box p={15} className={classes.boxContainer}>
            <h3>{t('terms-and-conditions')}</h3>
            <h4>{t('providing-smart-governance-information')}</h4>
            {params.map(renderText)}
            {renderForm(config)}
          </Box>
        </div>
        <SectionsValidationModal
          open={openValidate}
          unsatisfiedSections={unsatisfiedSections}
          onClose={() => setOpenValidate(false)}
        />
        <AlertDialog
          header={alert.header}
          message={alert.message}
          button={alert.button}
          onClose={() => setOpen(false)}
          isOpen={open}
        ></AlertDialog>
      </StyledConfirmAndSubmitPage>
    );
  };

  if (loading) {
    return (
      <div style={{ margin: 20 }}>
        <CircularProgress />
      </div>
    );
  }

  return <SmeConfigContext.Consumer>{renderConfirmPage}</SmeConfigContext.Consumer>;
}

export default ConfirmAndSubmitPage;
